import { Helmet } from 'react-helmet-async';

const SITE_ORIGIN = 'https://rlxhk.com';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Set on routes that vite-plugin-route-html.ts prerenders into their own
   * dist/<route>/index.html. Those files already carry a route-correct canonical
   * and JSON-LD in the served markup. Helmet does not deduplicate against static
   * tags it did not create, so re-emitting them here produced two <link rel=canonical>
   * and two FAQPage blocks in the live DOM. Skip them and let the static file own it.
   *
   * Only affects client-side navigation, where the head keeps the previously loaded
   * route's canonical. Crawlers and social scrapers always direct-load a URL, so they
   * get the prerendered file's correct tags.
   */
  staticHead?: boolean;
}

/**
 * Per-route SEO tags. Sets unique title, description, canonical, og:url, og:title,
 * og:description for each page. Falls back to index.html sitewide tags for non-JS crawlers.
 */
const SEO = ({ title, description, path, noindex, jsonLd, staticHead }: SEOProps) => {
  const url = `${SITE_ORIGIN}${path}`;
  const blocks = staticHead || !jsonLd ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {!noindex && !staticHead && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
